import React, { useState } from 'react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Package, Tag, Star, ClipboardList, Trash2, Plus, ImagePlus, X, Upload, Loader2, Gift,
} from 'lucide-react';
import { useStore } from '../../context/StoreContext';
import { apiError } from '../../api';
import { ProductsTab, SaleShippingTab } from './CatalogEditors';
import { CURRENCY } from '../../mock/mock';
import { toast } from 'sonner';
import { WhatsAppIcon, WHATSAPP_DISPLAY } from '../Effects';

const Field = ({ label, children }) => (
  <div>
    <Label className="text-xs text-muted-foreground">{label}</Label>
    <div className="mt-1">{children}</div>
  </div>
);

// Reusable file uploader button -> returns backend url
const UploadButton = ({ accept, label, onUploaded }) => {
  const { uploadFile } = useStore();
  const [busy, setBusy] = useState(false);
  const ref = React.useRef(null);
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const { url } = await uploadFile(file);
      await onUploaded(url, file);
      toast.success('File uploaded. Save your product, combo or review to apply it.');
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setBusy(false);
      if (ref.current) ref.current.value = '';
    }
  };
  return (
    <>
      <input ref={ref} type="file" accept={accept} aria-label={label || 'Upload file'} className="hidden" onChange={handle} />
      <Button type="button" variant="outline" className="shrink-0" disabled={busy} onClick={() => ref.current?.click()}>
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
        {label ? <span className="ml-1.5">{label}</span> : null}
      </Button>
    </>
  );
};


const ReviewsTab = () => {
  const { reviews, products, addReview, deleteReview } = useStore();
  const [form, setForm] = useState({ name: '', productId: products[0]?.id || '', rating: 5, comment: '' });
  const [media, setMedia] = useState([]);
  const [mediaInput, setMediaInput] = useState({ type: 'image', url: '' });
  const [busy, setBusy] = useState(false);
  const [deleting, setDeleting] = useState('');

  const addMedia = () => {
    if (!mediaInput.url) return;
    setMedia((m) => [...m, mediaInput]);
    setMediaInput({ type: 'image', url: '' });
  };

  const submit = async () => {
    if (busy) return;
    if (!form.name.trim() || !form.comment.trim()) { toast.error('Add reviewer name and comment.'); return; }
    setBusy(true);
    try {
      await addReview({ ...form, productId: form.productId || products[0]?.id || '', rating: Number(form.rating), media, verified: true });
      setForm({ name: '', productId: products[0]?.id || '', rating: 5, comment: '' });
      setMedia([]);
      toast.success('Review published to the website!');
    } catch (error) { toast.error(apiError(error)); }
    finally { setBusy(false); }
  };

  const remove = async review => {
    if (deleting || !window.confirm(`Delete the review by ${review.name}?`)) return;
    setDeleting(review.id);
    try { await deleteReview(review.id); toast.success('Review removed'); }
    catch (error) { toast.error(apiError(error)); }
    finally { setDeleting(''); }
  };

  return (
    <div className="grid lg:grid-cols-2 gap-6">
      <div className="border border-border rounded-xl p-5 bg-white space-y-4 h-fit">
        <h4 className="font-display font-semibold text-foreground flex items-center gap-2"><Plus className="h-4 w-4 text-primary" /> Add Review</h4>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Reviewer Name"><Input aria-label="Reviewer Name" disabled={busy} value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></Field>
          <Field label="Rating">
            <Select value={String(form.rating)} onValueChange={(v) => setForm({ ...form, rating: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {[5, 4, 3, 2, 1].map((n) => <SelectItem key={n} value={String(n)}>{n} Star{n > 1 ? 's' : ''}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <Field label="Product">
          <Select value={form.productId || products[0]?.id || ''} disabled={busy} onValueChange={(v) => setForm({ ...form, productId: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {products.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Comment"><Textarea aria-label="Review Comment" disabled={busy} rows={3} value={form.comment} onChange={(e) => setForm({ ...form, comment: e.target.value })} /></Field>

        <div>
          <Label className="text-xs text-muted-foreground">Add Photo / Video (paste URL)</Label>
          <div className="flex gap-2 mt-1">
            <Select value={mediaInput.type} onValueChange={(v) => setMediaInput({ ...mediaInput, type: v })}>
              <SelectTrigger className="w-28"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="image">Image</SelectItem>
                <SelectItem value="video">Video (YT embed)</SelectItem>
              </SelectContent>
            </Select>
            <Input value={mediaInput.url} onChange={(e) => setMediaInput({ ...mediaInput, url: e.target.value })} placeholder={mediaInput.type === 'video' ? 'https://www.youtube.com/embed/...' : 'https://image-url'} />
            <Button type="button" onClick={addMedia} variant="outline" className="shrink-0"><ImagePlus className="h-4 w-4" /></Button>
          </div>
          <div className="mt-2">
            <UploadButton
              accept="image/*,video/*"
              label="Upload photo / video from device"
              onUploaded={(url, file) => setMedia((m) => [...m, { type: file.type.startsWith('video') ? 'video' : 'image', url }])}
            />
          </div>
          {media.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {media.map((m, i) => (
                <span key={i} className="inline-flex items-center gap-1 bg-secondary text-xs px-2 py-1 rounded-full">
                  {m.type} #{i + 1}
                  <button onClick={() => setMedia(media.filter((_, idx) => idx !== i))}><X className="h-3 w-3" /></button>
                </span>
              ))}
            </div>
          )}
        </div>

        <Button onClick={submit} disabled={busy} className="w-full bg-primary hover:bg-primary/90 text-white font-semibold">{busy ? 'Publishing…' : 'Publish Review'}</Button>
      </div>

      <div className="space-y-3">
        <h4 className="font-display font-semibold text-foreground">Published Reviews ({reviews.length})</h4>
        <div className="space-y-3 max-h-[420px] overflow-y-auto pr-1">
          {reviews.map((r) => (
            <div key={r.id} className="border border-border rounded-xl p-4 bg-white">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-semibold text-sm">{r.name} <span className="text-muted-foreground font-normal">· {r.rating}★</span></p>
                  <p className="text-xs text-muted-foreground">{products.find((p) => p.id === r.productId)?.name}</p>
                </div>
                <button aria-label={`Delete review by ${r.name}`} disabled={!!deleting} onClick={() => remove(r)} className="text-muted-foreground hover:text-[#e11d1d] disabled:opacity-50">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
              <p className="text-sm text-foreground/70 mt-2">{r.comment}</p>
              {r.media?.length > 0 && <p className="text-xs text-primary mt-1">{r.media.length} media attached</p>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const STATUSES = ['Placed', 'Confirmed', 'Shipped', 'Delivered', 'Cancelled'];
const STATUS_STYLE = {
  Placed: 'bg-slate-100 text-slate-700', Confirmed: 'bg-blue-100 text-blue-700', Shipped: 'bg-amber-100 text-amber-800',
  Delivered: 'bg-green-100 text-green-700', Cancelled: 'bg-red-100 text-red-700',
};
const statusMessage = (o) => {
  const hi = `Hi ${o.customer.name}, `;
  const ref = `your FBD Cleaning order *${o.id}* (${CURRENCY}${o.total})`;
  return {
    Placed: `${hi}we have received ${ref}. We'll confirm it shortly.`,
    Confirmed: `${hi}${ref} is *confirmed*! We'll dispatch it very soon.`,
    Shipped: `${hi}${ref} has been *shipped* and is on its way. Please keep ${CURRENCY}${o.total} ready (Cash on Delivery).`,
    Delivered: `${hi}${ref} has been *delivered*. Thank you for choosing FBD Cleaning! We'd love your feedback.`,
    Cancelled: `${hi}${ref} has been *cancelled*. For any help, reply here or call ${WHATSAPP_DISPLAY}.`,
  }[o.status] + `\n\n– Team FBD Cleaning`;
};
const notifyCustomer = (o) => {
  const phone = o.customer.phone.replace(/\D/g, '').slice(-10);
  window.open(`https://wa.me/91${phone}?text=${encodeURIComponent(statusMessage(o))}`, '_blank');
};

const StatusSteps = ({ status }) => {
  const flow = STATUSES.slice(0, 4);
  const idx = flow.indexOf(status);
  if (status === 'Cancelled') return <Badge className="bg-red-100 text-red-700 hover:bg-red-100">Cancelled</Badge>;
  return (
    <div className="flex items-center gap-1">
      {flow.map((st, i) => (
        <React.Fragment key={st}>
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${i <= idx ? 'bg-primary text-white' : 'bg-secondary text-muted-foreground'}`}>{st}</span>
          {i < flow.length - 1 && <span className={`h-0.5 w-3 ${i < idx ? 'bg-primary' : 'bg-border'}`} />}
        </React.Fragment>
      ))}
    </div>
  );
};

const emptyCombo = { name: '', tagline: '', description: '', productIds: [], price: 0, image: '', active: true };

const ComboEditor = ({ initial, onSave, onCancel, saving }) => {
  const { products } = useStore();
  const [c, setC] = useState(initial);
  const original = c.productIds.reduce((s, id) => s + (products.find((p) => p.id === id)?.price || 0), 0);
  const toggle = (id) => setC({ ...c, productIds: c.productIds.includes(id) ? c.productIds.filter((x) => x !== id) : [...c.productIds, id] });
  return (
    <div data-testid="combo-editor" className="border border-primary/30 rounded-xl p-5 bg-white space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <Field label="Combo Name"><Input data-testid="combo-name-input" value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} /></Field>
        <Field label="Tagline"><Input data-testid="combo-tagline-input" value={c.tagline} onChange={(e) => setC({ ...c, tagline: e.target.value })} /></Field>
      </div>
      <Field label="Description"><Textarea rows={2} value={c.description} onChange={(e) => setC({ ...c, description: e.target.value })} /></Field>
      <div>
        <Label className="text-xs text-muted-foreground">Products in combo</Label>
        <div className="mt-2 flex flex-wrap gap-2">
          {products.map((p) => (
            <button type="button" key={p.id} data-testid={`combo-pick-${p.id}`} onClick={() => toggle(p.id)}
              className={`text-xs font-medium px-3 py-1.5 rounded-full border transition-colors ${c.productIds.includes(p.id) ? 'bg-primary text-white border-primary' : 'bg-white border-border hover:border-primary'}`}>
              {p.name}
            </button>
          ))}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-3 items-end">
        <Field label={`Combo Price (${CURRENCY}) · items total ${CURRENCY}${original}`}>
          <Input data-testid="combo-price-input" type="number" min="0.01" step="0.01" value={c.price} onChange={(e) => setC({ ...c, price: e.target.value })} />
        </Field>
        <p className="text-sm font-semibold text-[#159a3b] pb-2">
          {original > 0 && c.price > 0 ? `Customer saves ${CURRENCY}${original - c.price} (${Math.round(((original - c.price) / original) * 100)}%)` : ''}
        </p>
      </div>
      <Field label="Custom image (optional – defaults to product bottles)">
        <div className="flex gap-2">
          <Input value={c.image} onChange={(e) => setC({ ...c, image: e.target.value })} />
          <UploadButton accept="image/*" label="Upload" onUploaded={(url) => setC({ ...c, image: url })} />
        </div>
      </Field>
      <div className="flex items-center gap-3">
        <Switch checked={c.active} onCheckedChange={(v) => setC({ ...c, active: v })} id="combo-active" />
        <Label htmlFor="combo-active" className="text-sm">Show on website</Label>
      </div>
      <div className="flex gap-2">
        <Button data-testid="combo-save-btn" disabled={saving} onClick={() => {
          if (!c.name.trim() || c.productIds.length < 2 || !Number.isFinite(Number(c.price)) || Number(c.price) <= 0) { toast.error('Add a name, at least 2 products and a valid positive price.'); return; }
          onSave({ ...c, price: Number(c.price) });
        }} className="bg-primary hover:bg-primary/90 text-white">{saving ? 'Saving…' : 'Save Combo'}</Button>
        <Button variant="outline" disabled={saving} onClick={onCancel}>Cancel</Button>
      </div>
    </div>
  );
};

const CombosTab = () => {
  const { combos, products, createCombo, updateCombo, deleteCombo } = useStore();
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState('');
  const save = async (c) => {
    if (saving) return;
    setSaving(true);
    const { id, ...body } = c;
    try {
      if (id) await updateCombo(id, body); else await createCombo(body);
      toast.success('Combo saved');
      setEditing(null);
    } catch (error) { toast.error(apiError(error)); }
    finally { setSaving(false); }
  };
  const remove = async combo => {
    if (deleting || !window.confirm(`Delete ${combo.name}? Existing orders will remain.`)) return;
    setDeleting(combo.id);
    try { await deleteCombo(combo.id); toast.success('Combo deleted'); }
    catch (error) { toast.error(apiError(error)); }
    finally { setDeleting(''); }
  };
  return (
    <div className="space-y-4">
      {editing ? (
        <ComboEditor initial={editing} onSave={save} saving={saving} onCancel={() => setEditing(null)} />
      ) : (
        <Button data-testid="combo-new-btn" onClick={() => setEditing(emptyCombo)} className="bg-primary hover:bg-primary/90 text-white"><Plus className="h-4 w-4 mr-1.5" /> New Combo</Button>
      )}
      {combos.map((c) => (
        <div key={c.id} data-testid={`staff-combo-${c.id}`} className="border border-border rounded-xl p-4 bg-white flex items-center gap-4">
          <div className="flex -space-x-4">
            {c.productIds.map((id) => {
              const p = products.find((x) => x.id === id);
              return p ? <img key={id} src={p.image} alt={p.name} className="h-12 w-auto object-contain" /> : null;
            })}
          </div>
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-foreground">{c.name} {!c.active && <Badge variant="outline" className="ml-1 text-[10px]">Hidden</Badge>}</p>
            <p className="text-xs text-muted-foreground">{c.productIds.length} products · {CURRENCY}{c.price}</p>
          </div>
          <Button data-testid={`combo-edit-${c.id}`} size="sm" variant="outline" onClick={() => setEditing(c)}>Edit</Button>
          <button data-testid={`combo-delete-${c.id}`} aria-label={`Delete ${c.name}`} disabled={!!deleting} onClick={() => remove(c)} className="text-muted-foreground hover:text-[#e11d1d] disabled:opacity-50"><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
    </div>
  );
};

const OrdersTab = () => {
  const { orders, updateOrderStatus, ordersLoading, ordersError, reloadOrders } = useStore();
  const [saving, setSaving] = useState('');
  const changeStatus = async (o, v) => {
    if (saving) return;
    setSaving(o.id);
    try { await updateOrderStatus(o.id, v); toast.success(`Order ${o.id} → ${v}`, { action: { label: 'Notify on WhatsApp', onClick: () => notifyCustomer({ ...o, status: v }) } }); }
    catch (error) { toast.error(apiError(error)); }
    finally { setSaving(''); }
  };
  if (ordersLoading && !orders.length) return <p role="status">Loading orders…</p>;
  if (ordersError) return <div role="alert" className="space-y-3"><p>Could not load retail orders. Your saved orders have not been changed.</p><Button variant="outline" onClick={reloadOrders}>Retry orders</Button></div>;
  if (orders.length === 0) return <p className="text-muted-foreground text-center py-10">No orders yet. Orders placed by customers will appear here.</p>;
  return (
    <div className="space-y-4">
      {orders.map((o) => (
        <div key={o.id} data-testid={`order-${o.id}`} className="border border-border rounded-xl p-4 bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-semibold text-foreground">{o.id} <span className="text-xs text-muted-foreground">· {new Date(o.date).toLocaleString('en-IN')}</span></p>
              <p className="text-sm text-muted-foreground">{o.customer.name} · {o.customer.phone} · {o.customer.city} - {o.customer.pincode}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{o.customer.address}</p>
            </div>
            <div className="text-right">
              <p className="font-display font-bold text-primary">{CURRENCY}{o.total}</p>
              <p className="text-xs text-muted-foreground">COD · {o.items.reduce((s, i) => s + i.qty, 0)} items</p>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-1.5 text-xs text-muted-foreground">
            {o.items.map((i) => <span key={i.id} className="bg-secondary px-2 py-1 rounded-full">{i.qty}× {i.name}</span>)}
          </div>
          <div className="mt-3"><StatusSteps status={o.status} /></div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            <Label className="text-xs">Status:</Label>
            <Select value={o.status} disabled={!!saving} onValueChange={(v) => changeStatus(o, v)}>
              <SelectTrigger data-testid={`order-status-select-${o.id}`} className={`w-40 h-9 font-semibold ${STATUS_STYLE[o.status] || ''}`}><SelectValue /></SelectTrigger>
              <SelectContent>{STATUSES.map((st) => <SelectItem key={st} value={st}>{st}</SelectItem>)}</SelectContent>
            </Select>
            <Button data-testid={`order-notify-btn-${o.id}`} size="sm" onClick={() => notifyCustomer(o)} className="bg-[#25D366] hover:bg-[#1eb355] text-white h-9">
              <WhatsAppIcon className="h-4 w-4 mr-1.5" /> Send "{o.status}" update
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
};

const StaffPanel = () => (
  <Tabs defaultValue="orders" className="flex flex-col">
    <TabsList className="flex justify-start overflow-x-auto w-full h-auto [&>button]:shrink-0">
      <TabsTrigger data-testid="staff-tab-orders" value="orders"><ClipboardList className="h-4 w-4 mr-1.5" /> Orders</TabsTrigger>
      <TabsTrigger data-testid="staff-tab-products" value="products"><Package className="h-4 w-4 mr-1.5" /> Products</TabsTrigger>
      <TabsTrigger data-testid="staff-tab-combos" value="combos"><Gift className="h-4 w-4 mr-1.5" /> Combos</TabsTrigger>
      <TabsTrigger data-testid="staff-tab-sale" value="sale"><Tag className="h-4 w-4 mr-1.5" /> Sale & Shipping</TabsTrigger>
      <TabsTrigger data-testid="staff-tab-reviews" value="reviews"><Star className="h-4 w-4 mr-1.5" /> Reviews</TabsTrigger>
    </TabsList>
    <div className="pt-6">
      <TabsContent value="orders" className="mt-0"><OrdersTab /></TabsContent>
      <TabsContent value="products" className="mt-0"><ProductsTab UploadButton={UploadButton} /></TabsContent>
      <TabsContent value="combos" className="mt-0"><CombosTab /></TabsContent>
      <TabsContent value="sale" className="mt-0"><SaleShippingTab /></TabsContent>
      <TabsContent value="reviews" className="mt-0"><ReviewsTab /></TabsContent>
    </div>
  </Tabs>
);

export default StaffPanel;
